// ─────────────────────────────────────────────
// KLOTHIQ — Product Code & SKU Generator Utility
// Controlled abbreviation dictionary & structured generator
// ─────────────────────────────────────────────

export const PRODUCT_TYPE_CODES: Record<string, string> = {
  JERSEY: 'JER',
  TSHIRT: 'TSH',
  SHIRT: 'SHT',
  POLO: 'PLO',
  HOODIE: 'HDY',
  TOP: 'TOP',
  SHORTS: 'SHR',
  PANTS: 'PNT',
};

export const TEAM_CODES: Record<string, string> = {
  SPAIN: 'ESP',
  ARGENTINA: 'ARG',
  BRAZIL: 'BRA',
  PORTUGAL: 'POR',
  GERMANY: 'GER',
  FRANCE: 'FRA',
  ENGLAND: 'ENG',
  BARCELONA: 'BAR',
  'FC BARCELONA': 'BAR',
  'REAL MADRID': 'RMA',
  'MANCHESTER UNITED': 'MUN',
  'MANCHESTER CITY': 'MCI',
  'INTER MIAMI': 'IMI',
  INTER_MIAMI: 'IMI',
  ARSENAL: 'ARS',
  'ARSENAL FC': 'ARS',
  'AC MILAN': 'ACM',
  CHELSEA: 'CHE',
  LIVERPOOL: 'LIV',
  JUVENTUS: 'JUV',
  BAYERN: 'BAY',
  PSG: 'PSG',
};

export const EDITION_CODES: Record<string, string> = {
  HOME: 'HOME',
  AWAY: 'AWAY',
  THIRD: 'THIRD',
  RETRO: 'RETRO',
  SPECIAL: 'SP',
  'SPECIAL EDITION': 'SP',
  GOALKEEPER: 'GK',
  GK: 'GK',
  TRAINING: 'TRN',
};

export const SLEEVE_CODES: Record<string, string> = {
  'HALF SLEEVE': 'HS',
  'FULL SLEEVE': 'FS',
  SLEEVELESS: 'SL',
  '3/4 SLEEVE': '34S',
};

export const COLLAR_CODES: Record<string, string> = {
  COLLAR: 'CLR',
  'POLO COLLAR': 'CLR',
  'SPREAD COLLAR': 'CLR',
  'BUTTON-DOWN COLLAR': 'CLR',
  'MANDARIN COLLAR': 'CLR',
  'BAND COLLAR': 'CLR',
};

export const COLOR_CODES: Record<string, string> = {
  BLACK: 'BLK',
  WHITE: 'WHT',
  BLUE: 'BLU',
  RED: 'RED',
  GREEN: 'GRN',
  NAVY: 'NVY',
  YELLOW: 'YEL',
  PINK: 'PNK',
  GREY: 'GRY',
  GRAY: 'GRY',
  'OFF YELLOW': 'OY',
};

/**
 * Gets a clean abbreviation from dictionary or falls back to uppercase alphanumeric slice.
 */
export function getAbbreviation(
  dict: Record<string, string>,
  input?: string | null,
  fallbackLen: number = 3
): string {
  if (!input || !input.trim()) return '';
  const cleanInput = input.trim().toUpperCase();
  if (dict[cleanInput]) return dict[cleanInput];
  // Check substring matches
  for (const [key, code] of Object.entries(dict)) {
    if (cleanInput === key || cleanInput.includes(key) || key.includes(cleanInput)) {
      return code;
    }
  }
  // Fallback: take first N alphanumeric characters
  const alphanumeric = cleanInput.replace(/[^A-Z0-9]/g, '');
  return alphanumeric.slice(0, fallbackLen);
}

/**
 * Extract structured details from a composite product name if explicit inputs are blank.
 * e.g. "Spain Away Half Sleeve Jersey | Pedri 20"
 */
export function parseProductNameFallback(name: string) {
  let player = '';
  let number = '';
  let edition = '';
  let sleeve = '';

  const nameUpper = name.toUpperCase();

  // Extract Player & Number after pipe '|'
  if (name && name.includes('|')) {
    const parts = name.split('|');
    if (parts[1]) {
      const playerPart = parts[1].trim();
      const match = playerPart.match(/^([A-Za-z\s]+)\s*#?(\d+)?$/);
      if (match) {
        player = match[1] ? match[1].trim() : '';
        number = match[2] || '';
      } else {
        player = playerPart;
      }
    }
  }

  // Edition checks
  if (nameUpper.includes('RETRO')) edition = 'RETRO';
  else if (nameUpper.includes('AWAY')) edition = 'AWAY';
  else if (nameUpper.includes('THIRD')) edition = 'THIRD';
  else if (nameUpper.includes('HOME')) edition = 'HOME';
  else if (nameUpper.includes('SPECIAL')) edition = 'SPECIAL EDITION';

  // Sleeve checks
  if (nameUpper.includes('HALF SLEEVE') || nameUpper.includes('HS')) sleeve = 'Half Sleeve';
  else if (nameUpper.includes('FULL SLEEVE') || nameUpper.includes('FS')) sleeve = 'Full Sleeve';
  else if (nameUpper.includes('SLEEVELESS')) sleeve = 'Sleeveless';

  return { player, number, edition, sleeve };
}

export interface ProductCodeParams {
  productTypeName?: string; // e.g. "Jersey"
  teamName?: string;        // e.g. "Spain"
  edition?: string;         // e.g. "Away"
  sleeve?: string;          // e.g. "Half Sleeve"
  collar?: string;          // e.g. "Polo Collar"
  zipper?: boolean;         // e.g. true
  player?: string;          // e.g. "Pedri"
  playerNumber?: string | number; // e.g. 20
  productName?: string;     // Fallback parser if fields are partially empty
}

/**
 * Builds code prefix WITHOUT sequence:
 * KQ-[TYPE]-[TEAM]-[EDITION]-[SLEEVE]-[COLLAR]-[ZIPPER]-[PLAYER]-[NUMBER]
 */
export function buildProductCodePrefix(params: ProductCodeParams): string {
  const parts: string[] = ['KQ'];

  const fallback = params.productName ? parseProductNameFallback(params.productName) : { player: '', number: '', edition: '', sleeve: '' };

  // 1. Product Type
  const typeCode = getAbbreviation(PRODUCT_TYPE_CODES, params.productTypeName, 3);
  if (typeCode) parts.push(typeCode);

  // 2. Team
  const teamCode = getAbbreviation(TEAM_CODES, params.teamName, 3);
  if (teamCode) parts.push(teamCode);

  // 3. Edition
  const effEdition = params.edition || fallback.edition;
  const editionCode = getAbbreviation(EDITION_CODES, effEdition, 4);
  if (editionCode) parts.push(editionCode);

  // 4. Sleeve
  const effSleeve = params.sleeve || fallback.sleeve;
  const sleeveCode = getAbbreviation(SLEEVE_CODES, effSleeve, 2);
  if (sleeveCode) parts.push(sleeveCode);

  // 5. Collar
  const collarCode = getAbbreviation(COLLAR_CODES, params.collar, 3);
  if (collarCode) parts.push(collarCode);

  // 6. Zipper
  if (params.zipper) parts.push('ZPR');

  // 7. Player
  const effPlayer = params.player || fallback.player;
  if (effPlayer && effPlayer.trim()) {
    const cleanPlayer = effPlayer.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleanPlayer) parts.push(cleanPlayer);
  }

  // 8. Player Number
  const effNumber = (params.playerNumber !== undefined && params.playerNumber !== null && String(params.playerNumber).trim() !== '')
    ? params.playerNumber
    : fallback.number;
  if (effNumber !== undefined && effNumber !== null && String(effNumber).trim() !== '') {
    const numStr = String(effNumber).trim().replace(/[^0-9]/g, '');
    if (numStr) {
      parts.push(numStr.padStart(2, '0'));
    }
  }

  return parts.join('-');
}

/**
 * Formats complete Product Code with sequence number (3 digits):
 * e.g. KQ-JER-ESP-AWAY-HS-PEDRI-20-001
 */
export function formatProductCode(prefix: string, sequence: number): string {
  const seqStr = String(sequence).padStart(3, '0');
  return `${prefix}-${seqStr}`;
}

export const STANDARD_SIZE_SEQUENCES = [
  ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'],
  ['2-3Y', '3-4Y', '5-6Y', '7-8Y', '9-10Y', '11-12Y', '13-14Y'],
  ['28', '30', '32', '34', '36', '38', '40', '42', '44'],
  ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45'],
];

/**
 * Auto-advances to the next logical size when duplicating a variant row.
 * e.g., 'S' -> 'M', 'M' -> 'L', 'L' -> 'XL', '28' -> '30', '2-3Y' -> '3-4Y'
 */
export function getNextSize(currentSize?: string): string {
  if (!currentSize || !currentSize.trim()) return 'M';
  const clean = currentSize.trim().toUpperCase();

  // 1. Search standard size sequences
  for (const seq of STANDARD_SIZE_SEQUENCES) {
    const idx = seq.findIndex((s) => s.toUpperCase() === clean);
    if (idx !== -1 && idx < seq.length - 1) {
      const next = seq[idx + 1];
      if (next) return next;
    }
  }

  // 2. Numeric progression fallback (e.g. 28 -> 30, 30 -> 32)
  const num = parseInt(clean, 10);
  if (!isNaN(num)) {
    return String(num % 2 === 0 ? num + 2 : num + 1);
  }

  return clean;
}

/**
 * Builds Variant SKU:
 * KQ-JER-ESP-AWAY-HS-PEDRI-20-001-[COLOR]-[SIZE] or KQ-JER-ESP-AWAY-HS-PEDRI-20-001-[SIZE]
 */
export function buildVariantSku(productCode: string, color?: string, size?: string): string {
  const parts = [productCode];
  if (color && color.trim()) {
    const colorCode = getAbbreviation(COLOR_CODES, color, 3);
    if (colorCode) parts.push(colorCode);
  }
  if (size && size.trim()) {
    const cleanSize = size.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
    if (cleanSize) parts.push(cleanSize);
  }
  return parts.join('-');
}
